import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { useClientConfig, useUser } from "@/api/queries";
import { Spinner } from "@/components/Spinner";
import { ErrorPanel } from "@/components/ErrorPanel";
import { useAuth } from "./AuthProvider";

/** The backend's config couldn't be loaded, so sign-in isn't possible. */
export function BootError() {
  return (
    <div className="boot-error" role="alert" data-testid="boot-error">
      <b>We can&rsquo;t reach WealthyPlanet right now.</b>
      <span>Check your connection and reload the page.</span>
    </div>
  );
}

/** Not signed in: to /signin, coming back here afterwards. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { ready, user, unavailable } = useAuth();
  const location = useLocation();
  if (unavailable) return <BootError />;
  if (!ready) return <Spinner label="Signing you in" />;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signin?next=${next}`} replace />;
  }
  return <>{children}</>;
}

/** When the backend requires it, an unverified email goes to the verify-email gate. */
export function RequireVerified({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { data: config } = useClientConfig();
  if (config?.requireEmailVerification && user && !user.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }
  return <>{children}</>;
}

/** `hasFamily=false` means onboarding hasn't been saved yet: to /start. */
export function RequireFamily({ children }: { children: ReactNode }) {
  const userQuery = useUser();
  if (userQuery.isPending) return <Spinner label="Loading your plan" />;
  if (userQuery.isError)
    return <ErrorPanel error={userQuery.error} onRetry={() => userQuery.refetch()} />;
  if (!userQuery.data.hasFamily) return <Navigate to="/start" replace />;
  return <>{children}</>;
}

/** Admins and super admins only. `allowBlogAdmin` also admits the Firebase `blogAdmin` claim. */
export function RequireAdmin({
  children,
  allowBlogAdmin = false,
  superAdminOnly = false,
}: {
  children: ReactNode;
  allowBlogAdmin?: boolean;
  superAdminOnly?: boolean;
}) {
  const { user } = useAuth();
  const userQuery = useUser();
  const [blogAdmin, setBlogAdmin] = useState<boolean | null>(allowBlogAdmin ? null : false);

  useEffect(() => {
    if (!allowBlogAdmin || !user) return;
    let live = true;
    user
      .getIdTokenResult()
      .then((t) => live && setBlogAdmin(t.claims.blogAdmin === true))
      .catch(() => live && setBlogAdmin(false));
    return () => {
      live = false;
    };
  }, [allowBlogAdmin, user]);

  if (userQuery.isPending || blogAdmin === null) return <Spinner label="Checking access" />;
  const u = userQuery.data;
  const allowed = superAdminOnly
    ? !!u?.isSuperAdmin
    : !!(u?.isAdmin || u?.isSuperAdmin || (allowBlogAdmin && blogAdmin));
  if (!allowed) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

/** Wraps a route in auth + verification (no household needed). */
export function SignedIn({ children }: { children: ReactNode }) {
  return (
    <RequireAuth>
      <RequireVerified>{children}</RequireVerified>
    </RequireAuth>
  );
}
